import React from 'react';
import { Lock } from 'lucide-react';
import { rememberLoginMethod, type LoginMethod } from '@/utils/lastLoginMethod';
import LastUsedBadge from './LastUsedBadge';
import GithubMark from './GithubMark';
import GoogleMark from './GoogleMark';

export type OmniauthProvider = 'github' | 'google_oauth2';

interface SocialButtonsProps {
  githubPath: string;
  googlePath: string;
  ssoPath?: string;
  lastUsedMethod?: LoginMethod | null;
  authAction: 'login' | 'signup';
  /** OmniAuth provider name to hide (`github` / `google_oauth2`), so a user bounced back from it doesn't loop. */
  hiddenProvider?: OmniauthProvider | null;
}

const csrfToken = () => document.querySelector<HTMLMetaElement>("[name='csrf-token']")?.content ?? '';

interface ProviderButtonProps {
  action: string;
  method: LoginMethod;
  token: string;
  lastUsed: boolean;
  authAction: 'login' | 'signup';
  children: React.ReactNode;
}

const ProviderButton = ({ action, method, token, lastUsed, authAction, children }: ProviderButtonProps) => (
  <form method="post" action={action} target="_blank" rel="noopener noreferrer" className="relative">
    <input type="hidden" name="authenticity_token" value={token} />
    {lastUsed ? <LastUsedBadge /> : null}
    <button
      type="submit"
      id={`${authAction}-page-${method}-social-${authAction}-button`}
      title={`Continue with ${method === 'github' ? 'GitHub' : 'Google'}`}
      data-login-method={method}
      data-insight={JSON.stringify({ event: `click-${authAction}-button`, provider: method })}
      onClick={() => rememberLoginMethod(method)}
      className="focus-base flex h-14 w-full items-center justify-center gap-2 rounded-md border border-neutral-300 bg-neutral-000 text-neutral-1300 transition-colors hover:bg-neutral-100 active:bg-neutral-200"
    >
      {children}
    </button>
  </form>
);

export function SocialButtons({
  githubPath,
  googlePath,
  ssoPath,
  lastUsedMethod,
  authAction,
  hiddenProvider,
}: SocialButtonsProps) {
  const token = csrfToken();

  return (
    <div className="flex flex-col gap-4">
      {hiddenProvider === 'github' ? null : (
        <ProviderButton
          action={githubPath}
          method="github"
          token={token}
          lastUsed={lastUsedMethod === 'github'}
          authAction={authAction}
        >
          <GithubMark className="size-[1.125rem]" />
          <span className="text-sm font-semibold text-neutral-1300">Continue with GitHub</span>
        </ProviderButton>
      )}

      {hiddenProvider === 'google_oauth2' ? null : (
        <ProviderButton
          action={googlePath}
          method="google"
          token={token}
          lastUsed={lastUsedMethod === 'google'}
          authAction={authAction}
        >
          <GoogleMark className="size-[1.125rem]" />
          <span className="text-sm font-semibold text-neutral-1300">Continue with Google</span>
        </ProviderButton>
      )}

      {ssoPath ? (
        <div className="relative">
          {lastUsedMethod === 'sso' ? <LastUsedBadge /> : null}
          <a
            href={ssoPath}
            onClick={() => rememberLoginMethod('sso')}
            className="focus-base flex h-14 w-full items-center justify-center gap-2 rounded-md border border-neutral-300 bg-neutral-000 text-neutral-1300 transition-colors hover:bg-neutral-100 active:bg-neutral-200"
          >
            <Lock className="size-[1.125rem]" strokeWidth={1.5} />
            <span className="text-sm font-semibold text-neutral-1300">Continue with SSO</span>
          </a>
        </div>
      ) : null}
    </div>
  );
}

export default SocialButtons;
